import React, { useState, useEffect } from 'react';
import { Warehouse } from 'lucide-react';
import api from '../../utils/api';
import LoadingSpinner from '../LoadingSpinner';

// Controlled rack + floor picker. value = { rackId, floor } | onChange({ rackId, floor }).
const RackSelector = ({ value, onChange }) => {
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/storage/racks')
      .then((r) => setRacks((r.data.data || []).filter((rk) => rk.type === 'storage' && rk.isActive)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const selectedRack = racks.find((rk) => rk._id === value?.rackId);

  const selectRack = (rackId) => {
    if (rackId === value?.rackId) return;
    onChange({ rackId, floor: '' });
  };
  const selectFloor = (floor) => onChange({ rackId: value?.rackId, floor });

  if (loading) return <LoadingSpinner text="Loading racks…" />;

  if (racks.length === 0) {
    return (
      <p className="text-sm text-zip-muted py-6 text-center">
        No storage racks configured yet. Set them up under Storage Setup.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {racks.map((rack) => {
          const selected = value?.rackId === rack._id;
          return (
            <button
              key={rack._id}
              type="button"
              onClick={() => selectRack(rack._id)}
              className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all duration-200 focus-ring
                ${selected
                  ? 'border-zip-red bg-zip-red/8 shadow-[0_0_0_1px_rgba(232,25,44,0.3),0_0_20px_rgba(232,25,44,0.1)]'
                  : 'border-zip-border bg-zip-card hover:border-zip-border/80 hover:bg-zip-hover'
                }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center
                ${selected ? 'bg-zip-red/20' : 'bg-zip-surface'}`}>
                <Warehouse size={22} className={selected ? 'text-zip-red' : 'text-zip-muted'} />
              </div>
              <div className="text-center">
                <div className={`font-bold text-lg tracking-tight ${selected ? 'text-zip-red' : 'text-zip-text'}`}>
                  {rack.name}
                </div>
                <div className="text-[11px] text-zip-muted mt-0.5 font-mono">{rack.code}</div>
              </div>
              {selected && (
                <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-zip-red" />
              )}
            </button>
          );
        })}
      </div>

      {selectedRack && (
        <div>
          <label className="block text-xs font-medium text-zip-muted mb-2">Select Floor</label>
          {(selectedRack.floors || []).length === 0 ? (
            <p className="text-sm text-zip-muted">This rack has no floors configured.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedRack.floors.map((f) => {
                const selected = value?.floor === f.label;
                return (
                  <button
                    key={f.label}
                    type="button"
                    onClick={() => selectFloor(f.label)}
                    className={`px-4 py-2 rounded-lg border text-sm font-mono font-medium transition-all duration-150 focus-ring
                      ${selected
                        ? 'border-zip-red bg-zip-red text-white'
                        : 'border-zip-border bg-zip-card text-zip-text hover:bg-zip-hover'
                      }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RackSelector;
